/**
 * ONNX Runtime Web 会话管理
 *
 * EP 分层（§4.1，T4 起 WebGPU 自动探测）：
 * - 增强：WebGPU（navigator.gpu 存在且未显式禁用时自动优先；单次 create 内
 *   ORT 按 EP 列表顺序回退，整链失败再落回本模块的 wasm 基线）
 * - 基线：WASM + SIMD + 多线程（crossOriginIsolated 时）
 * - 兜底：WASM 单线程（numThreads=1）
 *
 * WebGPU 运行时分发（T4 实测修正，详见 docs/adr/0003-webgpu-ep.md）：
 * - 1.30.0 `onnxruntime-web/webgpu` 子包实际加载 **asyncify 变体**（并非
 *   issue #1 时代认知的 jsep 文件名），ort-wasm-simd-threaded.asyncify.wasm
 *   实测 26,781,914B ≈ 25.54MiB，仍超 Pages 25MiB 单文件限制（clean-dist
 *   会强制移除）→ 不能自托管。
 * - 分发：glue .mjs（53KB）经 Vite ?url 同域资产（正确 MIME，绕开 ModelScope
 *   .mjs 返回 application/octet-stream 阻塞模块加载的历史难点）；
 *   .wasm 走 ModelScope resolve 直链（实测每跳 CORS *、边缘缓存 360 天）。
 *   wasmPaths 用对象形式 {mjs, wasm}（1.30 支持；wasm 指单 URL 时 locateFile
 *   恒返它——webgpu 会话只会加载 asyncify 变体，可接受）。
 * - wasm 基线不变：同域 /ort/（tools/copy-ort-runtime.mjs 构建期拷贝）。
 * - 两个子包各自内嵌一份 onnxruntime-common（env 相互独立），需分别配置；
 *   会话跨副本 duck-typing 兼容（run 链路只读 type/dims/data/location，
 *   输出张量仅消费 .data/.dims）。
 */
import * as ort from "onnxruntime-web/wasm";
// WebGPU glue（53KB）：同域内容哈希资产，仅 WebGPU 路径按需下载
import asyncifyMjsUrl from "onnxruntime-web/ort-wasm-simd-threaded.asyncify.mjs?url";

/**
 * WebGPU 运行时二进制（25.54MiB，超 Pages 25MiB 限制 → ModelScope 托管）。
 * 与 public/models/manifest.json 同仓（anjingcuc/grip-web-models），resolve
 * 302 → cdn-lfs-cn-1.modelscope.cn 每跳 CORS *（COEP require-corp 页面可用）。
 * 拉取失败由 getSession 的 wasm 基线回退兜底（现网行为不变）。
 */
const ASYNCIFY_WASM_URL =
  "https://modelscope.cn/models/anjingcuc/grip-web-models/resolve/master/ort/ort-wasm-simd-threaded.asyncify.wasm";

export type EpKind = "webgpu" | "wasm";

let configured = false;

function threadCount(): number {
  const isolated =
    typeof self !== "undefined" && self.crossOriginIsolated === true;
  const cores =
    typeof navigator !== "undefined" ? navigator.hardwareConcurrency ?? 1 : 1;
  let n = isolated ? Math.min(4, cores) : 1;
  // 调试/降档钩子（#15）——worker 内 localStorage 不可用（访问即抛），
  // 降档必须可经消息传入：① self.__gripOrtThreads（推理 worker 按请求设置）
  try {
    const wov = (self as { __gripOrtThreads?: unknown }).__gripOrtThreads;
    if (typeof wov === "number" && wov > 0) return Math.floor(wov);
  } catch {
    // self 不可用时忽略
  }
  // ② localStorage["grip:ortThreads"] = "1" 强制单线程（主线程 realm）
  try {
    const ov = Number(localStorage.getItem("grip:ortThreads"));
    if (Number.isFinite(ov) && ov > 0) n = Math.floor(ov);
  } catch {
    // localStorage 不可用时忽略
  }
  return n;
}

/** 各子包的 env 相互独立（common 被分别内嵌），逐份配置 */
function configureEnv(o: typeof ort): void {
  o.env.wasm.numThreads = threadCount();
  o.env.wasm.simd = true;
  // 日志降噪；错误仍走异常
  o.env.logLevel = "warning";
}

function configure(): void {
  if (configured) return;
  configureEnv(ort);
  // 1.30 的 wasmPaths：字符串=目录前缀。同域 /ort/（public/ort/，构建期从
  // node_modules 拷贝，不入 git）——wasm-only 变体只加载这两个文件；
  // 曾尝试 ModelScope 托管，但其 .mjs 返回 application/octet-stream，模块加载被拒。
  ort.env.wasm.wasmPaths = "/ort/";
  configured = true;
}

export function webgpuAvailable(): boolean {
  return (
    typeof navigator !== "undefined" && "gpu" in navigator && !!navigator.gpu
  );
}

/**
 * adapter 预探测（一次性缓存）：navigator.gpu 存在但 requestAdapter 为 null
 * 的环境（无 GPU/驱动黑名单/禁用硬件加速的 headless）直接判不可用，避免白加载
 * webgpu chunk 与 25.5MiB asyncify.wasm 后才失败回退。与 ORT 内部探测同源同义，
 * 不改变 EP 选择语义。
 */
let adapterProbe: Promise<boolean> | null = null;

function adapterUsable(): Promise<boolean> {
  if (!adapterProbe) {
    adapterProbe = (async () => {
      try {
        if (typeof navigator === "undefined" || !("gpu" in navigator) || !navigator.gpu) {
          return false;
        }
        // DOM lib 尚无 WebGPU 类型；按需收窄到本探测用到的最小接口
        const gpu = navigator.gpu as { requestAdapter(): Promise<unknown> };
        const adapter = await gpu.requestAdapter();
        return !!adapter;
      } catch {
        return false;
      }
    })();
    adapterProbe.catch(() => {
      adapterProbe = null;
    });
  }
  return adapterProbe;
}

/** 现场排查开关：localStorage["grip:ortEp"] = "wasm" 强制禁用 WebGPU 路径；
 *  worker realm 经 self.__gripOrtEp（#15：降级由主线程经推理请求传入） */
function webgpuDisabled(): boolean {
  if ((self as { __gripOrtEp?: unknown }).__gripOrtEp === "wasm") return true;
  try {
    return localStorage.getItem("grip:ortEp") === "wasm";
  } catch {
    return false;
  }
}

type OrtModule = typeof ort;

let webgpuOrtPromise: Promise<OrtModule> | null = null;

/**
 * 惰性加载 webgpu 子包（独立 chunk，无 WebGPU 设备永不下载）。
 * extern-wasm 导出条件（vite.config resolve.conditions）保证与基线同构：
 * 外载运行时文件，绝不混用 bundle 变体（issue #1：会挂起）。
 */
function loadWebGpuOrt(): Promise<OrtModule> {
  if (!webgpuOrtPromise) {
    webgpuOrtPromise = import("onnxruntime-web/webgpu").then((m) => {
      const o: OrtModule = m;
      configureEnv(o);
      // 对象形式：mjs=同域资产（MIME 正确）；wasm=ModelScope 直链
      // （locateFile 恒返它，本会话只加载 asyncify 变体）。
      o.env.wasm.wasmPaths = { mjs: asyncifyMjsUrl, wasm: ASYNCIFY_WASM_URL };
      return o;
    });
    // 加载/配置失败不缓存毒状态：下次可重试或直接走 wasm 基线
    webgpuOrtPromise.catch(() => {
      webgpuOrtPromise = null;
    });
  }
  return webgpuOrtPromise;
}

/**
 * create 成功后的 EP 生效证据（console 观测用）。
 * env.webgpu.device 在首个 webgpu 会话创建完成后可安全读取（早于会话创建
 * 读取会触发 device 创建副作用，故只在 create 之后调用）。
 */
async function logWebGpuEp(id: string, o: OrtModule): Promise<void> {
  try {
    const device = await o.env.webgpu.device;
    console.info(
      device
        ? `[ort] ${id}: WebGPU EP 生效（runtime=asyncify）`
        : `[ort] ${id}: WebGPU EP 未生效，ORT 已内部回退 wasm（runtime=asyncify）`,
    );
  } catch (e) {
    console.warn(`[ort] ${id}: WebGPU device 探测异常（视为未生效）：${String(e)}`);
  }
}

const sessions = new Map<string, Promise<ort.InferenceSession>>();

/**
 * WebGPU 会话创建串行链：1.30 webgpu EP 的设备/会话初始化不允许并发
 * （"another WebGPU EP inference session is being created"），而调用方
 * （compare/app/worker）普遍 Promise.all 并发建会话。plain wasm 创建不受此限，
 * 保持并发（现网行为）。
 */
let webgpuCreateChain: Promise<unknown> = Promise.resolve();

function enqueueWebGpuCreate<T>(task: () => Promise<T>): Promise<T> {
  const run = webgpuCreateChain.then(task, task);
  webgpuCreateChain = run.then(
    () => undefined,
    () => undefined,
  );
  return run;
}

/**
 * 模型级黑名单：某 id 在 WebGPU 环境已被证明可用（其它会话成功）后仍创建
 * 失败（如 decoder 触发 TransformerMemcpy 限制）→ 环境问题排除，是模型特性，
 * 本 realm 内后续直接走 wasm，省去每次必败的重试开销。环境未验证成功前不拉黑
 * （网络/初始化瞬态失败可能是环境问题，重试合理）。
 */
const webgpuBadModels = new Set<string>();
let webgpuEnvProven = false;

/**
 * 创建（或复用）推理会话。modelBytes 由 modelLoader 提供（已校验 SHA-256）。
 * 同 id 二次调用直接复用。
 *
 * EP 选择（T4）：默认自动探测——navigator.gpu 可用即先试
 * executionProviders:['webgpu','wasm']（ORT 在列表内按序回退，如 adapter 为
 * null 时本 create 内部降级 wasm）；整链失败（子包加载/运行时分发/会话创建）
 * 再落回与现网完全一致的 plain wasm 路径。opts.preferWebGpu===false 或
 * localStorage["grip:ortEp"]==="wasm" 可关掉自动探测。
 */
export function getSession(
  id: string,
  modelBytes: ArrayBuffer,
  opts: { preferWebGpu?: boolean } = {},
): Promise<ort.InferenceSession> {
  configure();
  let s = sessions.get(id);
  if (!s) {
    s = (async () => {
      if (
        opts.preferWebGpu !== false &&
        !webgpuDisabled() &&
        !webgpuBadModels.has(id) &&
        webgpuAvailable() &&
        (await adapterUsable())
      ) {
        try {
          const o = await loadWebGpuOrt();
          // 串行创建：规避 webgpu EP 并发初始化守卫（调用方并发 Promise.all）
          const session = await enqueueWebGpuCreate(() =>
            o.InferenceSession.create(modelBytes, {
              executionProviders: ["webgpu", "wasm"],
            }),
          );
          webgpuEnvProven = true;
          await logWebGpuEp(id, o);
          return session;
        } catch (e) {
          if (webgpuEnvProven) webgpuBadModels.add(id);
          // 优雅回退：现网行为不变（plain wasm + /ort/）
          console.warn(`[ort] WebGPU 路径失败，回退 WASM：${id} ${String(e)}`);
        }
      }
      return await ort.InferenceSession.create(modelBytes, {
        executionProviders: ["wasm"],
      });
    })();
    sessions.set(id, s);
    s.catch(() => sessions.delete(id)); // 失败不留毒缓存
  }
  return s;
}

export { ort };
